import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { isRunningInNativeApp } from '@muzly/shared';

import { detectPlatform, openInApp, parseLinkTarget } from '../utils';

interface LinkPageState {
  /** 이 history 항목에서 이미 앱 열기를 시도했다. */
  linkAttempted?: boolean;
}

/**
 * 공유 링크 게이트웨이 — `https://app.muzly.io/link?to=<앱 안의 경로>`. 다이나믹 링크 대신
 * 이 페이지가 "앱이 있으면 앱, 없으면 웹"을 가른다.
 *
 * - 앱 안(WebView): Universal Link 로 앱이 이 주소를 받았다. 목적지로 바로 간다.
 * - 모바일 브라우저: 앱 열기를 한 번 시도한다(`openInApp`). 같은 도메인 페이지 안에서는
 *   Universal Link 가 발동하지 않으므로 커스텀 스킴이 필요하다. 앱이 없으면 웹 목적지로.
 * - 데스크톱: 열 앱이 없다. 웹에서 목적지로.
 *
 * 시도는 history 항목에 표시해 둔다 — 뒤로 가기로 이 항목에 돌아오면 다시 시도하지 않고
 * 목적지로 간다. 안 그러면 앱과 이 페이지 사이를 빠져나갈 수 없다.
 *
 * 프로필 게이트 밖이다 — 목적지가 게이트 안이면 그쪽에서 초기 설정으로 보내고, 설정 뒤
 * 목적지로 돌아온다.
 */
export const LinkPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname, search, state } = useLocation();
  const target = parseLinkTarget(params.get('to'));
  // 열 앱이 없거나(데스크톱) 이미 앱 안이면 시도 없이 목적지로 — effect 와 화면이 같은 판정을 쓴다.
  const platform = detectPlatform();
  const skipsAttempt = platform === 'desktop' || isRunningInNativeApp();
  const attempted = useRef(false);
  // 대기 중인 앱 열기 폴백 — 사용자가 먼저 웹으로 가면 끈다. 언마운트 cleanup 으로 끄지
  // 않는다: StrictMode 의 가상 언마운트가 막 건 폴백을 꺼 버린다. 이 페이지를 SPA 안에서
  // 떠나는 길은 그 버튼뿐이고, 브라우저 이동은 문서와 함께 타이머도 내린다.
  const cancelFallback = useRef<() => void>(() => undefined);

  useEffect(() => {
    // StrictMode 의 이중 effect 에서 스킴을 두 번 치지 않는다.
    if (attempted.current) {
      return;
    }
    attempted.current = true;

    if (!target) {
      navigate('/', { replace: true });
      return;
    }
    if (skipsAttempt || (state as LinkPageState | null)?.linkAttempted) {
      navigate(target, { replace: true });
      return;
    }
    navigate(
      { pathname, search },
      { replace: true, state: { linkAttempted: true } satisfies LinkPageState },
    );
    cancelFallback.current = openInApp({
      path: target,
      platform,
    });
    // 마운트 때 한 번만 — 이 페이지가 하는 일은 도착 순간의 판정 하나다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!target || skipsAttempt) {
    return null;
  }

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-background px-6 text-center">
      <h1 className="leading-none">
        <img
          src="/brand/wordmark.png"
          alt={t('web.common.appName')}
          className="h-7 w-auto"
        />
      </h1>
      <p className="mt-8 text-lg font-bold text-foreground">
        {t('web.link.opening')}
      </p>
      <p className="mt-2 text-sm text-muted-foreground">{t('web.link.hint')}</p>
      <button
        type="button"
        onClick={() => {
          cancelFallback.current();
          navigate(target, { replace: true });
        }}
        className="mt-10 h-[50px] w-full max-w-[320px] rounded-full bg-primary text-base font-bold text-primary-foreground"
      >
        {t('web.link.continueWeb')}
      </button>
    </main>
  );
};
